import { useContext, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import TiktokPixel from "tiktok-pixel";
import { shareActivity, updateShareEvent, useIsFavorite } from "@/api/traveler";
import share from "@/assets/images/icons/share.svg";
import HexagonButton from "@/common/customElements/HexagonButton";
import ImageCarousel from "@/common/customElements/ImageCarousel";
import { CircularDiv, CustomDiv, InlineDiv } from "@/common/divs";
import { RegularText, SemiBoldText } from "@/common/texts";
import { FavoriteButton } from "@/components/activity/favoriteButton";
import categoriesJson from "@/components/home/json/categories.json";
import FirebaseContext from "@/context/firebaseContext/firebaseContext";
import { useGoogleTranslation } from "@/hooks/useGoogleTranslation";
import { useTrack } from "@/hooks/useTrack";
import { ActivityDetailGeoSearch } from "@/model";
import { CategoryType, FirebaseContextType } from "@/types/components";
import {
  NEUTRO_TITLES_COLOR,
  PRIMARY_BRAND_VARIANT_COLOR,
  PRIMARY_BUTTON_COLOR,
} from "@/utils/constants";
import {
  getSubCategoryTranslationKey,
  renderSubCategoryIcon,
} from "@/utils/functions";
import "react-toastify/dist/ReactToastify.css";

const ActivityHeader = ({
  activity,
}: {
  activity: ActivityDetailGeoSearch;
}) => {
  const { data: session } = useSession();
  const { t } = useTranslation();

  const filteredImages =
    (activity.images &&
      activity?.images.filter((image) => image.type !== "BANNER")) ??
    [];

  const { data: isFavoriteResult, isLoading } = useIsFavorite(activity.id, {
    query: { enabled: session !== null, refetchOnWindowFocus: false, retry: 0 },
  });
  const getImageBanner = () => {
    if (activity.images?.find((image) => image.type === "BANNER")) {
      const imageUrl = activity.images.find(
        (image) => image.type === "BANNER",
      )?.link;
      if (imageUrl) {
        if (!imageUrl.includes("cdn.filestackcontent.com")) {
          return imageUrl + `?height=${"375"}&format=webp&quality=100`;
        }
        return imageUrl.replace(
          "https://cdn.filestackcontent.com/",
          `https://cdn.filestackcontent.com/resize=height:${"375"}/`,
        );
      } else {
        return "https://placehold.co/600x400";
      }
    } else {
      return (
        process.env.NEXT_PUBLIC_DEFAULT_ACTIVITY_BANNER_IMAGE ??
        "https://placehold.co/600x400"
      );
    }
  };
  const getCategoryName = (category: string) => {
    const founded = categoriesJson.find(
      (element: CategoryType) => element.name === category,
    );
    if (founded) {
      return t(founded.i18_name);
    } else return category;
  };
  const isFavorite = useMemo(() => {
    return isFavoriteResult ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavoriteResult, isLoading]);

  const { getTranslation, translation } = useGoogleTranslation();
  useEffect(() => {
    getTranslation(activity.name);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const notify = () => toast(t("notifications.copied"));
  const { trackEvent, TrackCustomEvents } = useTrack();
  const { logAnalyticsEvent } = useContext(
    FirebaseContext,
  ) as FirebaseContextType;
  const router = useRouter();
  return (
    <>
      <div className="activity-header-container">
        <img
          alt="activityImage"
          width={"100%"}
          className="image-banner"
          src={getImageBanner()}
          fetchPriority={"high"}
        ></img>
      </div>

      <CustomDiv
        className="activity-header"
        width="100%"
        height="450px"
        paddingX="108px"
        style={{ paddingTop: "55px" }}
      >
        <InlineDiv
          className="activity-header-content"
          width="100%"
          style={{ gap: "20px" }}
        >
          {activity.images && (
            <Box
              sx={{
                background: NEUTRO_TITLES_COLOR,
                borderRadius: "12px",
              }}
            >
              {" "}
              <ImageCarousel
                images={
                  filteredImages.length > 0 ? filteredImages : activity.images
                }
              />{" "}
            </Box>
          )}
          <div>
            <InlineDiv className="tags-container" style={{ gap: "8px" }}>
              <CircularDiv
                style={{ cursor: "pointer" }}
                onClick={() => {
                  router.push(
                    "/home?veeSearch=true&community=" +
                      activity.community?.id +
                      "&city=" +
                      activity.city +
                      "&term=cat:" +
                      activity.category.id +
                      " " +
                      activity.category.name,
                  );
                }}
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <InlineDiv style={{ alignItems: "center", gap: "5px" }}>
                  <SemiBoldText
                    fontSize="14px"
                    color={NEUTRO_TITLES_COLOR}
                    style={{ marginTop: "-4px", lineHeight: "13px" }}
                  >
                    {getCategoryName(activity.category.name)}
                  </SemiBoldText>
                  <img
                    src={renderSubCategoryIcon(activity.category.id, 0)}
                    alt="subCategoryMarker"
                    className="image-subcategory-icon"
                  ></img>
                </InlineDiv>
              </CircularDiv>
              {activity.subcategory && (
                <CircularDiv
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (activity.subcategory) {
                      router.push(
                        "/home?veeSearch=true&community=" +
                          activity.community?.id +
                          "&city=" +
                          activity.city +
                          "&term=" +
                          activity.subcategory.name,
                      );
                    }
                  }}
                  background={PRIMARY_BUTTON_COLOR}
                  height="32px"
                  paddingX="16px"
                  width="fit-content"
                >
                  <InlineDiv style={{ alignItems: "center", gap: "5px" }}>
                    <SemiBoldText
                      fontSize="14px"
                      color={NEUTRO_TITLES_COLOR}
                      style={{ marginTop: "-4px", lineHeight: "13px" }}
                    >
                      {t(
                        getSubCategoryTranslationKey(
                          activity.subcategory.id ?? 0,
                        ),
                      )}
                    </SemiBoldText>
                    <img
                      src={renderSubCategoryIcon(
                        activity.category.id,
                        activity.subcategory.id ?? 0,
                      )}
                      alt="subCategoryMarker"
                      className="image-subcategory-icon"
                    ></img>
                  </InlineDiv>
                </CircularDiv>
              )}
            </InlineDiv>
            <div>
              <SemiBoldText
                className="activity-title"
                fontSize="32px"
                color={PRIMARY_BRAND_VARIANT_COLOR}
              >
                {translation.length > 0 ? translation : activity.name}
              </SemiBoldText>
              <InlineDiv className="activity-title-container">
                <RegularText
                  className="activity-subtitle"
                  fontSize="24px"
                  color={PRIMARY_BRAND_VARIANT_COLOR}
                >
                  {t("activity.from")} {activity.priceWithTaxFormatted}
                </RegularText>
              </InlineDiv>
            </div>
            <InlineDiv style={{ gap: "10px", marginTop: "15px" }}>
              {!isLoading && session !== null ?
                <FavoriteButton
                  id={activity.id}
                  isFavoriteActivity={isFavorite}
                  sx={{
                    width: "32px",
                    height: "32px",
                    // position: "absolute",
                  }}
                  activity={activity}
                ></FavoriteButton>
              : null}
              {/*<IconButton*/}
              {/*  sx={{ ...sx }}*/}
              {/*  onClick={() => {*/}
              {/*    addFavoriteTravelerActivity({*/}
              {/*      data: {*/}
              {/*        activityId: activity.id,*/}
              {/*      },*/}
              {/*    }).then(() => {*/}
              {/*      setIsFavorite(true);*/}
              {/*    });*/}
              {/*    // setOpen(true);*/}
              {/*  }}*/}
              {/*>*/}
              {/*  <FavoriteIcon*/}
              {/*    sx={{*/}
              {/*      width: "32px",*/}
              {/*      height: "32px",*/}
              {/*      "&:hover #svg-icon": {*/}
              {/*        stroke: PRIMARY_BUTTON_COLOR,*/}
              {/*      },*/}
              {/*      "& #svg-icon": {*/}
              {/*        stroke: PRIMARY_BRAND_VARIANT_COLOR,*/}
              {/*        fill: isFavorite ? PRIMARY_BUTTON_COLOR : "unset",*/}
              {/*      },*/}
              {/*    }}*/}
              {/*  ></FavoriteIcon>*/}
              {/*</IconButton>*/}
              {/*<HexagonButton*/}
              {/*  width="32px"*/}
              {/*  backgroundColor={PRIMARY_BUTTON_COLOR}*/}
              {/*  onClick={() => {}}*/}
              {/*>*/}
              {/*  {" "}*/}
              {/*  <Image*/}
              {/*    alt="icon"*/}
              {/*    width="16"*/}
              {/*    height="16"*/}
              {/*    src={like.src}*/}
              {/*    style={{*/}
              {/*      position: "absolute",*/}
              {/*      top: "22%",*/}
              {/*      left: "25%",*/}
              {/*    }}*/}
              {/*  />*/}
              {/*</HexagonButton>*/}
              <HexagonButton
                width="32px"
                backgroundColor={PRIMARY_BUTTON_COLOR}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  if (session) {
                    updateShareEvent();
                    shareActivity();
                  }

                  trackEvent({
                    name: TrackCustomEvents.EVENT_SHARED_PRODUCT,
                    properties: {
                      activity_id: activity.id,
                      activity_name: activity.name,
                      activity_category: activity.category,
                      activity_subcategory: activity.subcategory,
                    },
                  });
                  logAnalyticsEvent(
                    TrackCustomEvents.EVENT_SHARED_PRODUCT_WEB,
                    {
                      activity_id: activity.id,
                      activity_name: activity.name,
                      activity_category: activity.category,
                      activity_subcategory: activity.subcategory,
                    },
                  );
                  TiktokPixel.track("Share", {
                    activity_id: activity.id,
                    activity_name: activity.name,
                    activity_category: activity.category,
                    activity_subcategory: activity.subcategory,
                  });
                  notify();
                }}
              >
                {" "}
                <Image
                  alt="share"
                  width="16"
                  height="16"
                  src={share.src}
                  style={{
                    position: "absolute",
                    top: "22%",
                    left: "25%",
                  }}
                />
              </HexagonButton>
            </InlineDiv>
          </div>
        </InlineDiv>
        <ToastContainer></ToastContainer>
      </CustomDiv>
    </>
  );
};

export default ActivityHeader;
